import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { AdminPermission } from "../../api/session-api";
import { defaultAdminPath, hasSurfacePermission } from "../../auth/access-policy";
import { Button, StatePanel } from "../../components/primitives";
import { WorkspaceStartup } from "../../components/workspace-startup";
import { useAuth } from "./auth-context";
import { safeLoginReturn } from "./safe-login-return";

function SessionConnectionError() {
  const { refreshActor, logout } = useAuth();
  return (
    <StatePanel
      description="We couldn't check your access. Reconnect and try again."
      kind="error"
      title="Connection interrupted"
      action={
        <>
          <Button variant="primary" onClick={() => void refreshActor().catch(() => undefined)}>
            Try again
          </Button>
          <Button onClick={() => void logout()}>Sign out</Button>
        </>
      }
    />
  );
}

export function RequireAuthenticated() {
  const { status, workspace } = useAuth();
  const location = useLocation();
  if (status === "connection-error") return <SessionConnectionError />;
  if (status === "loading" || status === "warming")
    return <WorkspaceStartup progress={workspace} />;
  if (status === "authenticated") return <Outlet />;
  return <Navigate replace state={{ from: location }} to="/login" />;
}

export function RequireFirstFactor() {
  const { actor, status, workspace } = useAuth();
  if (status === "connection-error") return <SessionConnectionError />;
  if (status === "loading" || status === "warming")
    return <WorkspaceStartup progress={workspace} />;
  if (status === "first-factor") return <Outlet />;
  if (status === "authenticated") return <Navigate replace to={defaultAdminPath(actor)} />;
  return <Navigate replace to="/login" />;
}

export function SignedOutOnly() {
  const { actor, status, workspace } = useAuth();
  const location = useLocation();
  if (status === "connection-error") return <SessionConnectionError />;
  if (status === "loading" || status === "warming")
    return <WorkspaceStartup progress={workspace} />;
  return status === "authenticated" ? (
    <Navigate replace to={safeLoginReturn(location.state) ?? defaultAdminPath(actor)} />
  ) : (
    <Outlet />
  );
}

export function RequirePermission({
  permission,
  alternativePermissions,
}: {
  permission: AdminPermission;
  alternativePermissions?: readonly AdminPermission[] | undefined;
}) {
  const { actor } = useAuth();
  if (
    hasSurfacePermission(actor, {
      permission,
      ...(alternativePermissions ? { alternativePermissions } : {}),
    })
  )
    return <Outlet />;
  return (
    <StatePanel
      description="Your assigned role does not allow this operation."
      kind="error"
      title="Access denied"
    />
  );
}

export function AdminLanding() {
  const { actor } = useAuth();
  return <Navigate replace to={defaultAdminPath(actor)} />;
}
